import { MediaImage } from '@/lib/mediaDisplay';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  useSiteSettings,
  useUpdateSiteSettings,
  useThemes,
  useTheme,
  useAccountThemes,
  usePages,
  useBuildSite,
  useUploadMedia,
  useDeployState,
  useDeployToCloudflare,
  useAddCloudflareDomain,
  useCloudflareDomainStatus,
  useCloudflareDeployments,
  useSite,
  useUpdateSite,
  sitesApi,
  openExternal,
  useAuthStore,
} from '@wordpuppi/shared';
import { useQueryClient } from '@tanstack/react-query';
import { saveZipBlob, pickFilesDesktop } from '@/utils/desktopActions';
import { resolveSiteAssetUrl } from '@/lib/siteAssets';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle2, ImageOff, Loader2 } from 'lucide-react';

export const Route = createFileRoute('/sites/$siteSlug/settings/generation')({
  component: GenerationSettingsPage,
});

function GenerationSettingsPage() {
  const { siteSlug } = Route.useParams();
  const { data: settingsData, isLoading } = useSiteSettings(siteSlug);
  const { data: themesData } = useThemes();
  const { data: themeData } = useTheme(siteSlug);
  const { data: accountThemesData } = useAccountThemes();
  const { data: pagesData } = usePages(siteSlug);
  const queryClient = useQueryClient();
  const token = useAuthStore((state) => state.token);
  const updateSettings = useUpdateSiteSettings(siteSlug);
  const buildSite = useBuildSite(siteSlug);
  const uploadMedia = useUploadMedia(siteSlug);
  const { data: deployState } = useDeployState(siteSlug);
  const cfConnected = deployState?.data?.can_deploy ?? false;

  const [siteTitle, setSiteTitle] = useState('');
  const [siteDescription, setSiteDescription] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [language, setLanguage] = useState('en');
  const [author, setAuthor] = useState('');
  const [theme, setTheme] = useState('default');
  const [homepageSlug, setHomepageSlug] = useState('');
  const [outputDir, setOutputDir] = useState('');
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [faviconUrl, setFaviconUrl] = useState('');
  const [navCtaText, setNavCtaText] = useState('');
  const [navCtaUrl, setNavCtaUrl] = useState('');

  // #614 v2: favicon_url can be a theme-relative filename ("favicon.svg"),
  // not just a /media/ upload — resolve against the site's own URL, and never
  // show a broken-image glyph (error/unresolvable -> neutral placeholder).
  const faviconSrc = resolveSiteAssetUrl(faviconUrl, siteUrl);
  const [faviconError, setFaviconError] = useState(false);
  useEffect(() => {
    setFaviconError(false);
  }, [faviconSrc]);

  useEffect(() => {
    if (settingsData?.data) {
      const s = settingsData.data;
      setSiteTitle(s.title || '');
      setSiteDescription(s.description || '');
      setSiteUrl(s.url || '');
      setLanguage(s.language || 'en');
      setAuthor(s.author || '');
      setTheme(s.theme || 'default');
      setHomepageSlug(s.homepage_slug || '');
      setOutputDir(s.output_dir || '');
      setIncludeDrafts(s.include_drafts || false);
      setFaviconUrl(s.favicon_url || '');
      setNavCtaText(s.nav_cta_text || '');
      setNavCtaUrl(s.nav_cta_url || '');
    }
  }, [settingsData]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const themes = themesData?.data || [];
  const pages = pagesData?.data || [];
  const lastBuiltAt = settingsData?.data?.last_built_at;

  // #628 / #630 / #650: an applied blueprint (site default) shadows this dropdown —
  // the build resolves applied blueprint > `settings.theme`. Rather than let a pick
  // 409 post-hoc, pause the control with an explanation and point to the Theme page.
  const appliedAccountId = settingsData?.data?.account_theme_id ?? null;
  const appliedBlueprintName =
    (appliedAccountId != null
      ? (accountThemesData?.data ?? []).find((t) => t.id === appliedAccountId)?.name
      : null) ??
    // legacy fork dir not yet imported into a blueprint (AB#650 imports lazily)
    (themeData?.data?.source === 'custom'
      ? themes.find((t) => t.id === themeData?.data?.forked_from)?.name ?? 'this theme'
      : null);
  const themePaused = appliedBlueprintName != null;

  const handleSaveSSGSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateSettings.mutateAsync({
        title: siteTitle || undefined,
        description: siteDescription || undefined,
        url: siteUrl || undefined,
        language: language || undefined,
        author: author || undefined,
        theme: theme || undefined,
        homepage_slug: homepageSlug || undefined,
        output_dir: outputDir || undefined,
        include_drafts: includeDrafts,
        favicon_url: faviconUrl || undefined,
        nav_cta_text: navCtaText || undefined,
        nav_cta_url: navCtaUrl || undefined,
      });
      toast.success('SSG settings updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update settings');
    }
  };

  const handleBuild = async () => {
    try {
      const result = await buildSite.mutateAsync();
      if (result.success) {
        // AB#673: a real build (data.success) has something to look at.
        const previewUrl = result.data?.success ? result.data.preview_url : undefined;
        toast.success(result.data?.message, {
          action: previewUrl
            ? { label: 'Open preview', onClick: () => void openExternal(previewUrl) }
            : undefined,
        });
        queryClient.invalidateQueries({ queryKey: ['sites', 'settings', siteSlug] });
      } else {
        toast.error(result.data?.message);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Build failed');
    }
  };

  const handleDownload = () => {
    const downloadUrl = sitesApi.getDownloadUrl(siteSlug);
    fetch(downloadUrl, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Download failed');
        return res.blob();
      })
      // AB#491: desktop writes via the native save dialog (WKWebView ignores
      // <a download>); web keeps the anchor/blob path. saveZipBlob branches.
      .then((blob) => saveZipBlob(blob, `${siteSlug}-build.zip`))
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : 'Download failed');
      });
  };

  const uploadFavicon = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    try {
      const result = await uploadMedia.mutateAsync(file);
      if (result.data?.url) {
        setFaviconUrl(result.data.url);
        toast.success('Favicon uploaded');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const formatBuildTime = (isoDate: string | undefined) => {
    if (!isoDate) return null;
    return new Date(isoDate).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Static Site Generation</CardTitle>
          <CardDescription>Configure how your site is built and exported</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveSSGSettings} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="siteTitle">Site Title</Label>
              <Input
                id="siteTitle"
                type="text"
                value={siteTitle}
                onChange={(e) => setSiteTitle(e.target.value)}
                placeholder="My Blog"
              />
              <p className="text-sm text-muted-foreground">
                Used in the page title and feed metadata
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="siteDescription">Site Description</Label>
              <Textarea
                id="siteDescription"
                value={siteDescription}
                onChange={(e) => setSiteDescription(e.target.value)}
                placeholder="A blog about..."
                rows={2}
              />
              <p className="text-sm text-muted-foreground">
                Used in SEO meta tags and feed metadata
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="siteUrl">Site URL</Label>
              <Input
                id="siteUrl"
                type="url"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://example.com"
              />
              <p className="text-sm text-muted-foreground">
                Base URL for canonical links and sitemap
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Input
                  id="language"
                  type="text"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  placeholder="en"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="author">Author</Label>
                <Input
                  id="author"
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Your Name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="theme">Theme</Label>
              <Select value={theme} onValueChange={setTheme} disabled={themePaused}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {themes.length === 0 && <SelectItem value="default">Default</SelectItem>}
                  {themes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {themePaused && (
                <p className="text-sm text-muted-foreground">
                  This site builds with its default blueprint &quot;{appliedBlueprintName}&quot;.
                  The bundled theme is paused while one is set — manage blueprints on the{' '}
                  <Link
                    to="/sites/$siteSlug/theme"
                    params={{ siteSlug }}
                    className="underline"
                  >
                    Theme page
                  </Link>
                  .
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="homepageSlug">Homepage</Label>
              <Select value={homepageSlug} onValueChange={setHomepageSlug}>
                <SelectTrigger>
                  <SelectValue placeholder="Use default index (post listing)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Use default index (post listing)</SelectItem>
                  {pages.map((page) => (
                    <SelectItem key={page.id} value={page.slug}>
                      {page.title} (/{page.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Select a page to use as the homepage, or use the default post listing
              </p>
            </div>

            <div className="space-y-2">
              <Label>Favicon</Label>
              <div className="flex items-center gap-3">
                {faviconUrl &&
                  (faviconSrc && !faviconError ? (
                    <MediaImage
                      scopeKey={siteSlug}
                      src={faviconSrc}
                      alt=""
                      onError={() => setFaviconError(true)}
                      className="w-8 h-8 rounded border object-contain"
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded border bg-muted flex items-center justify-center text-muted-foreground"
                      title="Preview unavailable"
                    >
                      <ImageOff className="w-4 h-4" />
                    </div>
                  ))}
                <Input
                  type="file"
                  accept="image/*"
                  className="max-w-xs"
                  onClick={(e) => pickFilesDesktop(e, { multiple: false, accept: 'image/*' }, uploadFavicon)}
                  onChange={async (e) => {
                    await uploadFavicon(Array.from(e.target.files ?? []));
                    e.target.value = '';
                  }}
                />
                {faviconUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFaviconUrl('')}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Upload an image to use as the site favicon
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="navCtaText">Nav Button Text</Label>
                <Input
                  id="navCtaText"
                  type="text"
                  value={navCtaText}
                  onChange={(e) => setNavCtaText(e.target.value)}
                  placeholder="Join waitlist"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="navCtaUrl">Nav Button URL</Label>
                <Input
                  id="navCtaUrl"
                  type="text"
                  value={navCtaUrl}
                  onChange={(e) => setNavCtaUrl(e.target.value)}
                  placeholder="./index.html#subscribe"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="outputDir">Output Directory</Label>
              <Input
                id="outputDir"
                type="text"
                value={outputDir}
                onChange={(e) => setOutputDir(e.target.value)}
                placeholder="./output (default)"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="includeDrafts"
                checked={includeDrafts}
                onCheckedChange={(checked) => setIncludeDrafts(!!checked)}
              />
              <Label htmlFor="includeDrafts" className="font-normal cursor-pointer">
                Include draft posts in build
              </Label>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button type="submit" disabled={updateSettings.isPending}>
                {updateSettings.isPending ? 'Saving...' : 'Save SSG Settings'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleBuild}
                disabled={buildSite.isPending}
              >
                {buildSite.isPending ? 'Building...' : 'Build Site'}
              </Button>
              {lastBuiltAt && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleDownload}
                  title="Download build output as ZIP"
                >
                  Download
                </Button>
              )}
            </div>
            {lastBuiltAt && (
              <p className="text-sm text-muted-foreground">
                Last built: {formatBuildTime(lastBuiltAt)}
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      {cfConnected ? (
        <CloudflareDeployCard siteSlug={siteSlug} />
      ) : (
        <Card className="opacity-60">
          <CardHeader>
            <CardTitle>Deploy to Cloudflare Pages</CardTitle>
            <CardDescription>
              <Link
                to="/sites/$siteSlug/platforms"
                params={{ siteSlug }}
                className="underline"
              >
                Connect Cloudflare Pages
              </Link>
              {' '}on the Platforms page to enable one-click deployment.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cloudflare Deploy Card (with domain configuration)
// ---------------------------------------------------------------------------

function CloudflareDeployCard({ siteSlug }: { siteSlug: string }) {
  const queryClient = useQueryClient();
  const { data: siteData } = useSite(siteSlug);
  const { data: deploymentsData } = useCloudflareDeployments(siteSlug);
  const deployToCloudflare = useDeployToCloudflare();
  const updateSite = useUpdateSite();
  const addDomain = useAddCloudflareDomain();

  const [domainInput, setDomainInput] = useState(siteData?.data?.domain || '');
  const [domainDirty, setDomainDirty] = useState(false);

  useEffect(() => {
    if (siteData?.data && !domainDirty) {
      setDomainInput(siteData.data.domain || '');
    }
  }, [siteData, domainDirty]);

  const siteDomain = siteData?.data?.domain || '';
  const latestDeployment = deploymentsData?.[0];

  // Poll domain verification status (only when a domain is configured)
  const { data: domainStatuses } = useCloudflareDomainStatus({ enabled: !!siteDomain, siteSlug });
  const domainEntry = domainStatuses?.find((d) => d.name === siteDomain);
  const domainCfStatus = domainEntry?.status; // "active", "pending", "verifying", etc.

  const handleDeploy = async () => {
    try {
      const result = await deployToCloudflare.mutateAsync({ site_slug: siteSlug });
      toast.success(`Deployed! ${result.url}`);
      queryClient.invalidateQueries({ queryKey: ['sites', 'settings', siteSlug] });
      queryClient.invalidateQueries({ queryKey: ['cloudflare', 'domain-status'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Deployment failed', { duration: 20000 });
    }
  };

  const handleSaveDomain = async () => {
    try {
      await updateSite.mutateAsync({
        slug: siteSlug,
        data: { domain: domainInput || undefined },
      });
      setDomainDirty(false);

      // Also add the domain to the CF Pages project and update DNS
      if (domainInput) {
        try {
          const result = await addDomain.mutateAsync({ domain: domainInput, site_slug: siteSlug });
          if (result.dns_updated) {
            toast.success('Domain added to Cloudflare Pages and DNS updated');
          } else {
            toast.success('Domain added to Cloudflare Pages (DNS update failed — check your DNS zone)');
          }
        } catch {
          toast.success('Domain saved (will be added to Cloudflare Pages on next deploy)');
        }
      } else {
        toast.success('Domain removed');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update domain');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Deploy to Cloudflare Pages</CardTitle>
        <CardDescription>
          Build your site and deploy it to Cloudflare Pages in one step
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="cfDomain">Custom Domain</Label>
          <div className="flex gap-2">
            <Input
              id="cfDomain"
              type="text"
              value={domainInput}
              onChange={(e) => {
                setDomainInput(e.target.value);
                setDomainDirty(true);
              }}
              placeholder="blog.example.com"
              className="flex-1"
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleSaveDomain}
              disabled={updateSite.isPending || addDomain.isPending || !domainDirty}
            >
              {updateSite.isPending || addDomain.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
          {siteDomain && domainCfStatus === 'active' ? (
            <p className="text-sm text-success flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Active on Cloudflare Pages
            </p>
          ) : siteDomain && (domainCfStatus === 'verifying' || domainCfStatus === 'pending') ? (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Verifying on Cloudflare Pages...
            </p>
          ) : siteDomain && domainCfStatus ? (
            <p className="text-sm text-muted-foreground">
              Status: {domainCfStatus}
            </p>
          ) : siteDomain ? (
            <p className="text-sm text-muted-foreground">
              Will be added on next deploy
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Set a custom domain to use instead of the .pages.dev URL
            </p>
          )}
        </div>

        <Button
          onClick={handleDeploy}
          disabled={deployToCloudflare.isPending}
        >
          {deployToCloudflare.isPending ? 'Deploying...' : 'Build & Deploy'}
        </Button>
        {latestDeployment && (
          <p className="text-sm text-muted-foreground">
            Last deployment:{' '}
            <a
              href={latestDeployment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {latestDeployment.url}
            </a>
            {' '}
            ({new Date(latestDeployment.created_on).toLocaleString()})
          </p>
        )}
      </CardContent>
    </Card>
  );
}
